<template>
  <div>
    <a-table
      :columns="columns"
      :dataSource="bookings"
      :loading="loadStatus"
      :pagination="{ hideOnSinglePage: true }"
      :locale="{ emptyText: '' }"
      rowKey="id"
    >
      <!-- Booking ID Column -->
      <div slot="bookingId" slot-scope="record">
        <a-popover trigger="hover" @cancel="cancel" v-if="hasWatchlist(record)">
          <template slot="content">
            <div>Click to remove booking from the watchlist</div>
          </template>
          <a-icon
            type="star"
            style="cursor: pointer"
            @click="removeFromWatchlist(record)"
            theme="twoTone"
            twoToneColor="#F2C007"
          />
        </a-popover>

        <a-popover v-else-if="addingWatchlist(record)">
          <template slot="content">
            <span>Adding to the Watchlist</span>
          </template>
          <a-icon type="loading" />
        </a-popover>

        <a-popover trigger="hover" @cancel="cancel" v-else>
          <template slot="content">
            <div>Click to add booking to the watchlist</div>
          </template>
          <a-icon
            @click="addToWatchlist(record)"
            type="star"
            style="cursor:pointer;"
          />
        </a-popover>

        <a :href="'/send-freight/bookings/' + record.id"> {{ record.id }} </a>

        <a-popover v-if="record.isNetwork">
          <template slot="title">
            <span>Network Booking</span>
          </template>
          <template slot="content">
            <div style="max-width: 250px">
              These are bookings made by other Freighthub customers that are
              either being picked up from, or delivered to your business
            </div>
          </template>
          <span> <i class="fa fa-globe" aria-hidden="true"></i> </span>
        </a-popover>
      </div>
      <!-- End of Booking ID Column -->

      <!-- Location Name Column -->
      <div
        slot="locationName"
        slot-scope="record"
      >
        <!-- Location column popover -->
        <a-popover placement="top">
          <template slot="title">
            <span>{{ locationInfo(record).name }}</span>
          </template>
          <template slot="content">
            <div>
              <i class="fa fa-map-marker" aria-hidden="true"></i>
              <span>{{ locationInfo(record).address }}</span>
            </div>
            <div v-if="locationInfo(record).email">
              <i class="fa fa-envelope" aria-hidden="true"></i>
              <span>{{ locationInfo(record).email }}</span>
            </div>
            <div v-if="locationInfo(record).phone">
              <i class="fa fa-phone" aria-hidden="true"></i>
              <span>{{ locationInfo(record).phone }}</span>
            </div>
          </template>
          {{ locationInfo(record).name }}
        </a-popover>

        <!-- TailLift Icon -->
        <a-popover v-if="locationInfo(record).requiresTailLift" placement="top">
          <template slot="content">
              <span>{{ locationInfo(record).type }} requires a tail lift</span>
          </template>
          <span>
            <tail-lift-icon />
          </span>
        </a-popover>
      </div>
      <!-- End of location name column -->

      <!-- Status & Icons Column -->
      <div slot="status" slot-scope="record" class="d-flex justify-content-between">
        <div class="flex-fill">
          {{ record.status }}
        </div>

        <div class="mr-1">
          <!-- Loading Icon -->
          <a-popover v-if="record.pickupStop != null" placement="top">
            <template slot="content">
              <span>
                {{
                  getWaitingIconMouseOver(
                    Math.round(record.pickupStop.duration),
                    Math.round(record.pickupStop.waiting_time),
                    "pickup"
                  )
                }}
              </span>
            </template>
            <span>
              <load-truck
                :style="{color: getWaitingIconColor(Math.round(record.pickupStop.waiting_time))}"
              />
            </span>
          </a-popover>

          <!-- Unloading Icon -->
          <a-popover v-if="record.deliveryStop != null" placement="top">
            <template slot="content">
              <span>
                {{
                  getWaitingIconMouseOver(
                    Math.round(record.deliveryStop.duration),
                    Math.round(record.deliveryStop.waiting_time),
                    "deliver"
                  )
                }}
              </span>
            </template>
            <span>
              <unload-truck
                :style="{color: getWaitingIconColor(Math.round(record.deliveryStop.waiting_time))}"
              />
            </span>
          </a-popover>

          <!-- Source Icon -->
          <a-popover placement="top">
            <template slot="content">
              <span>
                {{ record.latestFreightEvent.type + " " }}
                {{ getSourceText(record.latestFreightEvent.source) }}
              </span>
            </template>
            <i
              :class="getSourceIcon(record.latestFreightEvent.source)"
              aria-hidden="true"
            ></i>
          </a-popover>

          <!-- Allocated Truck Icon -->
          <truck-map-modal
            v-if="record.latestAllocatedTruckPlate && record.status === 'Booked'"
            :truck-plate="record.latestAllocatedTruckPlate"
          />

          <!-- Dangerous Goods Icon -->
          <a-popover v-if="hasDangerousGoods(record)">
            <template slot="content">
              <span>Contains Dangerous Goods</span>
            </template>
            <span>
              <icon-font-cn type="iconskeleton" />
            </span>
          </a-popover>

          <!-- Tracker Icon -->
          <a-popover v-if="record.tracker">
            <template slot="content">
              <span>{{ getTrackerText(record.trackerStatusBand, record.status, record) }}</span>
            </template>
            <span>
              <i
                class="fas fa-link"
                :style="{color: getTrackerIconColor(record.trackerStatusBand, record.status)}"
              />
            </span>
          </a-popover>

          <!-- Add a tracker button and modal -->
          <a-popover v-if="bookingType != 'incoming' && !record.tracker && record.status_id >= 2 && record.status_id <= 3">
            <template slot="content">
              Freighthub provides free real-time tracking of your bookings.<br />
              Please call (09) 280 3639 to have your physical trackers couriered
              to you.
            </template>
            <a-button
              @click="onAddTrackerClick(record)"
              type="button"
              class="btn-outline-primary"
            >
              Add A Tracker
            </a-button>
          </a-popover>
        </div>
      </div>
      <!-- End of status column -->

      <!-- POD Column -->
      <a-popover slot="podTitle" placement="top">
        <template slot="content">
          <span>Proof of delivery</span>
        </template>
        POD
      </a-popover>

      <div slot="pod" slot-scope="record">
        <!--POD Icon: file text-->
        <a
          :href="'/send-freight/bookings/' + record.id + '/proof-of-delivery'"
          target="_blank"
          v-if="record.hasPod"
        >
          <i class="fa fa-file" aria-hidden="true"></i>
        </a>
      </div>

      <!-- Track booking button-->
      <div slot="track" slot-scope="record">
        <a-button type="primary" @click="trackBooking(record)">
          Track
        </a-button>
      </div>

      <!-- Deliver By Column -->
      <div slot="deliverBy" slot-scope="record">
        <div style="float: left" :class="{ 'deleted-date': record.timing > 0 }">
          {{ getCalendarTime(record.deliverBy) }}
        </div>
        <div v-if="record.timing > 0 && record.deliveryTime && record.status === 'Delivered'" style="float: left;margin-left: 5px;color: red">
          {{ getCalendarTime(record.deliveryTime) }}
        </div>
        <div v-else-if="record.timing > 0 && record.eta > moment()" style="float: left;margin-left: 5px;color: red">
          {{ getCalendarTime(record.eta) }}
        </div>

        <div style="float: right">
          <a-popover v-if="record.timing > 0" placement="top">
            <template slot="content">
              <span v-if="isUnknownDelay(record)">
                Behind the schedule, please
                <a href="/contact" target="_blank"> contact Freighthub</a> for
                more details
              </span>
              <span v-else>{{ getRedClockPopoverText(record) }}</span>
            </template>
            <div style="color: red">
              <i class="fa fa-clock" aria-hidden="true"></i
              >{{ "+" + record.timing }}
            </div>
          </a-popover>

          <!--Advanced Icon: green clock-->
          <a-popover v-if="record.timing < 0" placement="top">
            <template slot="content">
              <span>{{ getGreenClockPopoverText(record) }}</span>
            </template>
            <div style="color: green">
              <i class="fa fa-clock" aria-hidden="true"></i>{{ record.timing }}
            </div>
          </a-popover>
        </div>
      </div>
    </a-table>

    <div v-if="!bookings.length && !loadStatus" style="text-align: center;">
      <p>No bookings yet.</p>
      <a-button type="primary" size="large" :href="'new'">Add New Booking</a-button>
    </div>

    <!-- Tracker Modal -->
    <add-tracker-modal
      v-if="selectedBooking"
      :booking="selectedBooking"
      :is-showing-tracker-modal="isShowingTrackerModal"
      @modal-cancelled="trackerModalCancelled"
      @modal-submitted="trackerModalSubmitted"
    />

  </div>
</template>

<script>
import moment from "moment";
import TruckMapModal from "../modals/TruckMap";
import IconFontCn from "../icons/IconFontCn";
import LoadTruckIcon from "../icons/LoadTruckIcon";
import UnloadTruckIcon from "../icons/UnloadTruckIcon";
import TailLiftIcon from "../icons/TailLiftIcon";
import AddTrackerModal from "../modals/AddTrackerModal";

const statusOrder = [
  "Booked",
  "Loading",
  "In transit",
  "Unloading",
  "Delivered",
];

const sourceIcons = {
  GPS_SERVICE: "fa fa-location-arrow",
  API_INTEGRATION: "fa fa-server",
  USER_REQUEST: "fa fa-street-view",
};

const sourceText = {
  GPS_SERVICE: "detected from GPS",
  API_INTEGRATION: "confirmed by 3rd party API",
  USER_REQUEST: "confirmed by Freighthub",
};

export default {
  components: {
    IconFontCn,
    TruckMapModal,
    LoadTruckIcon,
    UnloadTruckIcon,
    TailLiftIcon,
    AddTrackerModal
  },
  props: {
    bookingType: {
      type: String,
      default: "",
    },
    emptyText: {
      type: String,
      default: "No Booking",
    },
    loadStatus: {
      type: Boolean,
      required: false,
    },
    bookings: Array,
  },
  data() {
    return {
      moment,
      value: [],
      expectedWaitingTime: 10,
      selectedBooking: null,
      isShowingTrackerModal: false,
    };
  },
  methods: {
    trackBooking(booking) {
      window.location.href = "/send-freight/bookings/" + booking.id;
    },
    getWaitingIconColor(waitingTime) {
      const diff = waitingTime - this.expectedWaitingTime;
      if (diff > 0) {
        return "red";
      }
      return "green";
    },
    getTrackerText(band, status, record) {
      if (status.includes('Booked')) {
        return 'This booking has a blue-tooth FreightMate attached to it to provide real-time tracking';
      }
      if (status === "Delivered") {
        return 'This booking has been delivered';
      }
      if (band == 0) {
        return 'We currently have no infomation about this tracker';
      }
      let lastPingAgo = moment.utc(record.tracker.last_location_time).fromNow()
      if (band == 1 || band == 2) {
        return "Moving (last detected " + lastPingAgo + ")";
      } else if (band == 5) {
        return "Last detected " + lastPingAgo;
      }
      let lastTruckStopAgo = moment.utc(record.latestFreightEvent.time).fromNow(true)
      return "Stopped for " + lastTruckStopAgo + " (last detected " + lastPingAgo + ")";
    },
    getTrackerIconColor(band, status) {
      if (status === "Delivered" || band == 0 || status.includes('Booked')) {
        return '';
      }
      if (band == 1) {
        return "green";
      } else if (band == 2) {
        return "orange";
      } else if (band == 3) {
        return "orange";
      } else if (band == 4) {
        return "red";
      } else if (band == 5) {
        return "blue";
      }
      return "";
    },
    getWaitingIconMouseOver(stopDuration, waitingTime, action) {
      const diff = waitingTime - this.expectedWaitingTime;
      let comparison = "as expected";
      if (diff > 0) {
        comparison = diff + " minutes more than expected";
      }
      if (diff < 0) {
        comparison = Math.abs(diff) + " minutes less than expected";
      }
      return `Truck stopped for ${stopDuration} minutes to ${action} freight, ${comparison}. `;
    },
    removeFromWatchlist(booking) {
      this.$store.dispatch("deleteBookingWatchlist", booking);
    },
    addToWatchlist(booking) {
      const params = { reason: "source: Sender Dashboard" };
      this.$store.dispatch("addBookingWatchlist", { booking, params });
    },
    cancel() {
      console.log("Click on No");
    },
    hasWatchlist(booking) {
      return booking.watchlist && Number.parseInt(booking.watchlist.id) > 0;
    },
    addingWatchlist(booking) {
      return booking.watchlist && Number.parseInt(booking.watchlist.id) === 0;
    },
    hasDangerousGoods(booking) {
      if (booking.lines.length < 1) {
        return false;
      }
      let dgLines = booking.lines.filter((line) =>
        line.goodsType.includes("Containing Dangerous Goods")
      );
      return dgLines.length > 0;
    },
    getSourceIcon(source) {
      return sourceIcons[source];
    },
    getSourceText(source) {
      return sourceText[source];
    },
    getCalendarTime(time) {
      return time
        ? time.calendar(null, {
            sameDay: "[Today]",
            nextDay: "[Tomorrow]",
            nextWeek: function callback(now) {
              return now.isoWeek() === time.isoWeek() ? "ddd" : "[Next] ddd";
            },
            lastDay: "[Yesterday]",
            lastWeek: function callback(now) {
              return now.isoWeek() === time.isoWeek() ? "ddd" : "[Last] ddd";
            },
            sameElse: "Do MMM",
          })
        : null;
    },
    isUnknownDelay(booking) {
      return booking.eta < moment() && booking.deliveryTime === null;
    },
    getRedClockPopoverText(booking) {
      if (booking.timing > 0) {
        return (
          Math.abs(booking.timing) +
          (Math.abs(booking.timing) > 1 ? " days" : " day") +
          " behind the schedule"
        );
      }
      return "Behind the schedule";
    },
    getGreenClockPopoverText(booking) {
      if (booking.timing < 0) {
        return (
          "Delivered " +
          Math.abs(booking.timing) +
          (Math.abs(booking.timing) > 1 ? " days" : " day") +
          " ahead of schedule"
        );
      }
      return "Delivered ahead of schedule";
    },

    /* Tracking Modal */
    onAddTrackerClick(record) {
      this.selectedBooking = record;
      this.isShowingTrackerModal = true;
    },

    trackerModalCancelled() {
      this.selectedBooking = null;
      this.isShowingTrackerModal = false;
    },

    trackerModalSubmitted() {
      this.selectedBooking = null;
      this.isShowingTrackerModal = false;
    },
  },
  computed: {
    /**
     * Returns an object related to the location of the booking. It adds the name, email, phone, and address
     * of the booking based on whether it's supposed to be a pickup or delivery. It also adds the type (pickup or
     * delivery) and whether the location requires a tail lift.
     *
     * @return - Object
     */
    locationInfo() {
      return (booking) => {
        let freightIsIncoming = (this.bookingType === "incoming");
        let locationType = (freightIsIncoming) ? "pickupLocation" : "deliveryLocation";
        let type = (freightIsIncoming) ? "Pickup" : "Delivery";
        let requiresTailLift = (freightIsIncoming) ? booking.pickupRequiresTailLift : booking.deliveryRequiresTailLift;

        return {
          ...booking[locationType],
          requiresTailLift: requiresTailLift,
          type: type
        };
      }
    },
    columns: function() {
      let freightIsIncoming = (this.bookingType === "incoming");
      let locationColumnField = (freightIsIncoming) ? "pickupLocation" : "deliveryLocation";

      return [
        {
          title: "Booking",
          key: "id",
          scopedSlots: { customRender: "bookingId" },
          sorter: (a, b) => a.id - b.id,
        },
        {
          title: (freightIsIncoming) ? "From" : "To",
          key: locationColumnField,
          scopedSlots: { customRender: "locationName" },
          sorter: (a, b) => a[locationColumnField].name <= b[locationColumnField].name ? -1 : 1,
          filters: [],
          onFilter: (value, record) => record[locationColumnField].name.indexOf(value) === 0
        },
        {
          title: "Status",
          key: "human_status",
          sorter: (a, b) => (statusOrder.indexOf(a.status) * 1000000000 - a.deliverBy.unix() >
            statusOrder.indexOf(b.status) * 1000000000 - b.deliverBy.unix()) ? 1 : -1,
          defaultSortOrder: "descend",
          onFilter: (value, record) => record.status.indexOf(value) === 0,
          scopedSlots: { customRender: "status" },
        },
        {
          title: "Deliver by",
          key: "deliverBy",
          sorter: (a, b) => (a.deliverBy.unix() > b.deliverBy.unix() ? 1 : -1),
          scopedSlots: { customRender: "deliverBy" },
        },
        {
          key: "pod",
          align: "center",
          slots: { title: "podTitle" },
          scopedSlots: { customRender: "pod" },
          filters: [
            { text: "Uploaded", value: "true" },
            { text: "Not Uploaded", value: "false" },
          ],
          onFilter: (value, record) => record.hasPod === (value === "true"),
        },
        {
          title: "Track",
          key: "track",
          scopedSlots: { customRender: "track" },
        },
      ];
    },
  },
};
</script>

<style scoped>
>>> .ant-table-tbody > tr > td {
  padding: 8px 16px;
}

.deleted-date {
  text-decoration: line-through;
}

/*overwrite svg in bootstrap css*/
>>> svg {
  vertical-align: unset;
}

>>> .ant-table-placeholder {
  border-bottom: 0px;
}
</style>
